import { Alert, Button, Snackbar, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DBTasksThunks, taskColumnSelectByQuery, tasksSelectByQuery } from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectDefaultPhoneCountry } from '@common-reducers/UserSelectors';
import { addCountryCode, findMissingCountryCode } from '@common/utils/phone-validation-utils';

interface Props {
    boardId: string;
}

interface Cell {
    taskId: string;
    columnId: string;
    value: string;
}

interface Found {
    callingCode: string;
    cells: Cell[];
}

const dismissKey = (boardId: string) => `bt-missing-country-code-dismissed:${boardId}`;

const readDismissed = (boardId: string): boolean => {
    try {
        return localStorage.getItem(dismissKey(boardId)) === '1';
    } catch {
        return false;
    }
};

const writeDismissed = (boardId: string): void => {
    try {
        localStorage.setItem(dismissKey(boardId), '1');
    } catch {
        // storage unavailable: the banner just returns next time
    }
};

/**
 * #873: numbers saved without the user's own country code ("51987654321" instead
 * of "5551987654321" for a Brazilian user) are read by WhatsApp as another
 * country's number and fail with "not on WhatsApp". We only SUGGEST the fix: the
 * same digits can be a genuine foreign number, so nothing is rewritten unless the
 * user clicks "add" — and that can be undone.
 *
 * Mount it with `key={boardId}` so a board switch starts from that board's own
 * "keep as is" choice.
 */
export const MissingCountryCodeBanner = ({ boardId }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const country = useAppSelector(selectDefaultPhoneCountry);
    const columns = useAppSelector((state) => taskColumnSelectByQuery(state.DBTaskColumnReducer, { boardId, deleted: false }));
    const tasks = useAppSelector((state) => tasksSelectByQuery(state.DBTasksReducer, { boardId, deleted: false }));
    const [dismissed, setDismissed] = useState(() => readDismissed(boardId));
    const [found, setFound] = useState<Found>();
    const [undo, setUndo] = useState<{ count: number; code: string; cells: Cell[] }>();
    const tasksRef = useRef(tasks);
    tasksRef.current = tasks;

    // The selectors return a fresh array on every render, so the effect is keyed on the
    // CONTENT of the whatsapp-id cells: keying on the arrays would re-run it (and re-set
    // state) after every render.
    const columnIds = (columns ?? []).filter((c) => c.type === 'whatsapp-id-cell' && c._id).map((c) => c._id as string);
    const cells: Cell[] = (tasks ?? []).flatMap((task) =>
        columnIds.flatMap((columnId) => {
            const value = task.customData?.[columnId]?.value;
            return typeof value === 'string' && value ? [{ taskId: task._id as string, columnId, value }] : [];
        })
    );
    const cellsKey = JSON.stringify(cells);

    useEffect(() => {
        if (dismissed) return;
        let cancelled = false;
        const current: Cell[] = JSON.parse(cellsKey);
        findMissingCountryCode(current.map((c) => c.value), country).then((missing) => {
            if (cancelled) return;
            setFound(missing ? { callingCode: missing.callingCode, cells: current.filter((c) => missing.suspects.includes(c.value)) } : undefined);
        }).catch(() => {
            // the metadata failed to load: no banner, nothing else depends on it
            if (!cancelled) setFound(undefined);
        });
        return () => {
            cancelled = true;
        };
    }, [cellsKey, country, dismissed]);

    /** Write `valueOf(cell)` into every cell, ONE patch per task built from its current customData, so two columns of one task don't overwrite each other. */
    const writeCells = (cells: Cell[], valueOf: (cell: Cell) => string) => {
        const byTask = new Map<string, Cell[]>();
        for (const cell of cells) byTask.set(cell.taskId, [...(byTask.get(cell.taskId) ?? []), cell]);
        for (const [taskId, taskCells] of byTask) {
            const task = tasksRef.current?.find((tk) => tk._id === taskId);
            if (!task) continue;
            const customData = { ...(task.customData ?? {}) };
            for (const cell of taskCells) customData[cell.columnId] = { ...(customData[cell.columnId] ?? {}), value: valueOf(cell) };
            dispatch(DBTasksThunks.patch({ entity: { _id: taskId, customData } }));
        }
    };

    const keep = () => {
        writeDismissed(boardId);
        setDismissed(true);
    };

    const addCode = () => {
        if (!found) return;
        writeCells(found.cells, (cell) => addCountryCode(cell.value, found.callingCode));
        setUndo({ count: found.cells.length, code: found.callingCode, cells: found.cells });
        setFound(undefined);
    };

    const undoAdd = () => {
        if (!undo) return;
        writeCells(undo.cells, (cell) => cell.value);
        setUndo(undefined);
    };

    return (
        <>
            {found && !dismissed && (
                <Alert
                    severity="warning"
                    data-testid="missing-country-code-banner"
                    sx={{ mb: 1 }}
                    action={
                        <Stack direction="row" spacing={1}>
                            <Button color="inherit" size="small" onClick={keep} data-testid="missing-country-code-keep">
                                {t('audience.missingCountryCode.keep', 'Keep as is')}
                            </Button>
                            <Button color="inherit" size="small" variant="outlined" onClick={addCode} data-testid="missing-country-code-add">
                                {t('audience.missingCountryCode.add', 'Add +{{code}}', { code: found.callingCode })}
                            </Button>
                        </Stack>
                    }
                >
                    {t('audience.missingCountryCode.message', '{{n}} numbers may be missing your country code (+{{code}}).', { n: found.cells.length, code: found.callingCode })}
                </Alert>
            )}
            <Snackbar
                open={!!undo}
                autoHideDuration={10000}
                onClose={(_, reason) => reason !== 'clickaway' && setUndo(undefined)}
                message={undo ? t('audience.missingCountryCode.added', 'Added +{{code}} to {{n}} numbers.', { code: undo.code, n: undo.count }) : ''}
                action={
                    <Button color="inherit" size="small" onClick={undoAdd} data-testid="missing-country-code-undo">
                        {t('audience.missingCountryCode.undo', 'Undo')}
                    </Button>
                }
            />
        </>
    );
};

export default MissingCountryCodeBanner;
